import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  getPromptPack,
  getStoryFilmExecution,
  getStoryFilmExecutionMedia,
  getStoryFilmExecutionReference,
  getStoryFilmAgentPreparation,
  getStoryFilmPreparationReference,
  listStoryFilmAgentRevisions,
  getStoryFilmAgentRevision,
  getStoryFilmAgentRevisionFilm,
  getStoryFilmAgentRevisionMedia,
  getStoryFilmAgentRevisionReference,
} from '../../api/story-film-prompt-packs.js'

export function useStoryFilmDetail() {
  const { t } = useI18n()

  const selectedExecutionId = ref('')
  const execution = ref(null)
  const executionLoading = ref(false)
  const executionError = ref('')
  const executionRefreshError = ref(false)
  const executionSnapshot = ref(null)
  const snapshotState = ref('idle')
  const predictedCurrency = ref(null)

  const selectedShotIndex = ref(null)
  const referenceUrls = ref({})
  const referenceStates = ref({})
  const referenceErrors = ref({})
  const referenceIdentities = new Map()

  // 独立准备工作区状态
  const selectedPreparation = ref(null)
  const selectedPreparationDetail = ref(null)
  const availableRevisions = ref([])
  const revisionName = ref('')
  const revisionLoading = ref(false)
  const revisionError = ref('')
  const independentRecord = ref(null)
  const independentLoading = ref(false)
  const independentError = ref('')

  let disposed = false
  let executionPollTimer = null
  let independentRequest = 0
  const executionViewMemory = new Map()
  const independentViewMemory = new Map()

  function recordErrorText(cause) {
    if (cause?.statusCode === 401) return t('storyFilm.executionLog.authRequired')
    if (cause?.statusCode === 403) return t('storyFilm.executionLog.recordForbidden')
    if (cause?.statusCode === 404) return t('storyFilm.executionLog.recordNotFound')
    return t('storyFilm.executionLog.recordLoadFailed')
  }

  function clearReferenceUrls() {
    for (const url of Object.values(referenceUrls.value)) URL.revokeObjectURL(url)
    referenceUrls.value = {}
    referenceStates.value = {}
    referenceErrors.value = {}
    referenceIdentities.clear()
  }

  function isCurrentSubmission(id) {
    return !disposed && String(selectedExecutionId.value) === String(id)
  }

  function stopExecutionPolling() {
    if (executionPollTimer !== null) clearTimeout(executionPollTimer)
    executionPollTimer = null
  }

  function hasRecordedCall(shot) {
    return shot?.call !== null && typeof shot?.call === 'object' && !Array.isArray(shot.call)
  }

  function referenceIdentity(shot, reference) {
    return JSON.stringify([shot.call?.call_id ?? null, reference?.matched === true, reference?.role ?? null, reference?.source ?? null, reference?.sha256 ?? null])
  }

  function reconcileReferences(shots) {
    const states = { ...referenceStates.value }
    const urls = { ...referenceUrls.value }
    const errors = { ...referenceErrors.value }
    const active = new Set()
    for (const shot of shots) {
      const references = hasRecordedCall(shot) ? shot.actual_references : shot.planned_references
      if (!Array.isArray(references)) continue
      for (const [index, reference] of references.entries()) {
        const key = `${shot.index}:${index}`
        const identity = referenceIdentity(shot, reference)
        active.add(key)
        if (referenceIdentities.get(key) === identity) continue
        if (urls[key]) URL.revokeObjectURL(urls[key])
        delete urls[key]
        delete errors[key]
        states[key] = reference?.matched === true ? 'idle' : 'unmatched'
        referenceIdentities.set(key, identity)
      }
    }
    for (const key of referenceIdentities.keys()) {
      if (active.has(key)) continue
      if (urls[key]) URL.revokeObjectURL(urls[key])
      delete urls[key]
      delete errors[key]
      delete states[key]
      referenceIdentities.delete(key)
    }
    referenceUrls.value = urls
    referenceStates.value = states
    referenceErrors.value = errors
  }

  async function loadSelectedReferences(id, shot) {
    const references = hasRecordedCall(shot) ? shot.actual_references : shot?.planned_references
    if (!Array.isArray(references)) return
    for (const [index, reference] of references.entries()) {
      const key = `${shot.index}:${index}`
      if (reference?.matched !== true || referenceStates.value[key] === 'ready' || referenceStates.value[key] === 'loading') continue
      referenceStates.value = { ...referenceStates.value, [key]: 'loading' }
      try {
        const blob = await getStoryFilmExecutionReference(id, shot.index, index)
        if (!isCurrentSubmission(id)) return
        referenceUrls.value = { ...referenceUrls.value, [key]: URL.createObjectURL(blob) }
        referenceStates.value = { ...referenceStates.value, [key]: 'ready' }
      } catch (cause) {
        if (!isCurrentSubmission(id)) return
        referenceErrors.value = { ...referenceErrors.value, [key]: cause?.message || '加载参考图失败' }
        referenceStates.value = { ...referenceStates.value, [key]: 'error' }
      }
    }
  }

  async function loadExecutionCurrency(result) {
    if (!['matched_by_operation_id', 'pinned_submission'].includes(result.pack_match) || !result.pack_id) {
      snapshotState.value = 'unavailable'
      return
    }
    snapshotState.value = 'loading'
    try {
      const snapshot = await getPromptPack(result.pack_id)
      if (isCurrentSubmission(result.submission_id)) {
        executionSnapshot.value = snapshot
        predictedCurrency.value = snapshot.request?.source?.quote?.currency ?? null
        snapshotState.value = 'ready'
      }
    } catch {
      if (isCurrentSubmission(result.submission_id)) snapshotState.value = 'unavailable'
    }
  }

  const selectedShot = computed(() => {
    return execution.value?.shots?.find(item => item.index === selectedShotIndex.value) ?? null
  })

  const selectedScene = computed(() => {
    const shot = selectedShot.value
    if (!shot || !execution.value?.screenplay?.scenes) return null
    return execution.value.screenplay.scenes.find(scene => {
      return (scene.beats ?? []).some(b => b.shot_index === shot.index)
    }) ?? null
  })

  const evidenceGroups = computed(() => {
    const shot = selectedShot.value
    if (!shot?.evidence?.turns) return []
    return shot.evidence.turns.map(turn => ({
      seq: turn.seq,
      label: `第 ${turn.seq} 回合证据`,
      passages: (turn.passages ?? []).map(p => ({
        speaker: p.speaker || '旁白',
        text: p.text || '',
        time: p.time,
      })),
    }))
  })

  const frozenPassages = computed(() => {
    return selectedShot.value?.evidence?.frozen_context?.passages ?? []
  })

  const castRoleList = computed(() => {
    const raw = selectedShot.value?.cast
    if (!raw) return []
    if (Array.isArray(raw)) return raw
    return [String(raw)]
  })

  const sourceStoryHref = computed(() => {
    const task = execution.value?.task
    if (!task?.run_id || !task?.branch_id) return ''
    return `/novel/runs/${encodeURIComponent(task.run_id)}?${new URLSearchParams({ branch_id: task.branch_id })}#story`
  })

  const promptComparison = computed(() => {
    const planned = selectedShot.value?.planned_prompt
    const actual = selectedShot.value?.actual_prompt
    if (typeof planned !== 'string' || !planned || typeof actual !== 'string' || !actual) return 'unknown'
    return planned === actual ? 'same' : 'different'
  })

  function selectShot(shot) {
    if (!shot) return
    selectedShotIndex.value = shot.index
    if (selectedExecutionId.value) {
      void loadSelectedReferences(selectedExecutionId.value, shot)
    }
  }

  function disposeDetail() {
    disposed = true
    stopExecutionPolling()
    clearReferenceUrls()
    independentRequest += 1
  }

  return {
    selectedExecutionId,
    execution,
    executionLoading,
    executionError,
    executionRefreshError,
    executionSnapshot,
    snapshotState,
    predictedCurrency,
    selectedShotIndex,
    selectedShot,
    selectedScene,
    evidenceGroups,
    frozenPassages,
    castRoleList,
    sourceStoryHref,
    promptComparison,
    referenceUrls,
    referenceStates,
    referenceErrors,
    selectedPreparation,
    selectedPreparationDetail,
    independentRecord,
    independentLoading,
    independentError,
    availableRevisions,
    revisionName,
    revisionLoading,
    revisionError,
    reconcileReferences,
    loadSelectedReferences,
    loadExecutionCurrency,
    selectShot,
    clearReferenceUrls,
    stopExecutionPolling,
    disposeDetail,
    recordErrorText,
  }
}

